import { ChangeDetectorRef, Component,HostListener, OnInit, ViewChild } from '@angular/core';
import { WorkflowApplicationsJanusData } from './workflow-applications-janus.model';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  ContractorService,
  DataService,
  SecurityService,
  WorkflowContextService,
  WorkflowService
} from 'src/app/services';
import { distinctUntilChanged, filter, finalize } from 'rxjs';
import { WorkflowApplicationsJanusService } from './workflow-applications-janus.service';
import {
  ItemSearchOptionField,
  ItemSearchOptionFieldOption
} from 'src/app/components/filter-list/models/filterClasses';
import { ApplicationStatus, WorkflowType } from 'src/app/models';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ModalConfirmComponent } from 'src/app/components/system/modal-confirm/modal-confirm.component';
import { BulkCloseApplicationRequest } from 'src/app/models/close-application-request';
import { ToastrService } from 'ngx-toastr';
import { FilterBuilderParam } from 'src/app/models/filter-builder';
import { myWorkOptions } from 'src/app/components/common/common-filterbuilder-staff-janus/common-filterbuilder-staff-janus.model';
import { svgs } from 'src/assets/export.assets';

interface BulkAction {
  name: string;
  id: string;
  action: (rows: any[]) => Promise<boolean>;
}
@Component({
  selector: 'app-workflow-applications-janus',
  standalone: false,
  templateUrl: './workflow-applications-janus.component.html',
  styleUrls: ['./workflow-applications-janus.component.scss']
})
export class WorkflowApplicationsJanusComponent implements OnInit {
  //  isRestoringFilters = true; //flag for avoid duplicate api trigger

  @ViewChild('bulkActionConfirm', { static: true })
  bulkActionConfirm: ModalConfirmComponent;
  workflowApplicationsJanusDetails: WorkflowApplicationsJanusData = {
    newApplications: [],
    displayNewApplications: [],
    activeNewApplicationId: '',
    errorNewApplicationFetch: '',
    isNewApplicationFetching: false,
    filterWorkflows: []
  };

  // Added by Amisha on 2026-07-29.
  // Reason: track the complete start-application loading/error lifecycle and reuse the shared error icon.
  isLoading = false;
  startApplicationError = false;
  errorIcon = svgs.errorIcon.name;
  loading = true;
  clientId: string;
  useNewLayout: boolean;

  parcelId = '';
  searchQuery: string = '';
  searchFieldConfig = {
    label: 'Search',
    type: 'text',
    variant: 'outline'
  };
  propertyOwner = '';
  propertyAddress = '';
  filterInputs: ItemSearchOptionField[] = [];
  showAdvancedFilter = false;
  placeholder = 'Search Applications';
  public readonly FILTER_CACHE_KEY = 'workflow-applications-filters';
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private context: WorkflowContextService,
    private cdr: ChangeDetectorRef,
    private workflowService: WorkflowService,
    private dataService: DataService,
    private workflowApplicationsJanusSvc: WorkflowApplicationsJanusService,

    private _securitySvc: SecurityService,
    private toast: ToastrService,

    private _contractorSvc: ContractorService
  ) {
    this.useNewLayout = context.useNewLayout;
  }

  isMobileView = window.innerWidth <= 959;

isBulkDrawerOpen = false;

@HostListener('window:resize')
onResize() {
  this.isMobileView = window.innerWidth <= 959;
}

openBulkDrawer() {
  if (!this.selected?.length) return;
  this.isBulkDrawerOpen = true;
}

closeBulkDrawer() {
  this.isBulkDrawerOpen = false;
}
  // added by priya 27-02-2026

  applicationAttributes: FilterBuilderParam[] = [];

  // Added by amisha on 2026-06-02 to centralize Janus attribute metadata for fields with special filter behavior.
  // Reason: the attribute dropdown now follows the live visible table headers while preserving date/range and option-based filters.
  private getAttributeConfigMap(): Record<string, Partial<FilterBuilderParam>> {
    return {
      APPLICATION_NUMBER: {
        types: ['contains', 'is']
      },
      ApplicationSubmittedOn: {
        types: ['is', 'range'],
        inputType: 'date'
      },
      WORKFLOW_NAME: {
        types: ['is', 'in'],
        options: this.workflowApplicationsJanusDetails.filterWorkflows || []
      },
      CreateDate: {
        types: ['is', 'range'],
        inputType: 'date'
      },
      IssuedOn: {
        types: ['is', 'range'],
        inputType: 'date'
      },
      PARCEL_ADDRESS: {
        types: ['contains', 'is']
      },
      SUBMITTER: {
        types: ['contains', 'is']
      },
      PARCEL_OWNER: {
        types: ['contains', 'is']
      },
      APPLICATION_STATUS: {
        types: ['is', 'in'],
        options: [
          { name: 'Not Started', value: '0' },
          { name: 'In Progress', value: '1' },
          { name: 'Completed', value: '2' },
          { name: 'Expired', value: '6' },
          { name: 'Locked', value: '7' },
          { name: 'Withdrawn', value: '8' },
          { name: 'Voided', value: '3' },
          { name: 'Rejected', value: '4' },
          { name: 'Discarded', value: '5' }
        ]
      }
    };
  }

  // Added by amisha on 2026-06-02 to rebuild Janus attribute options from the table's current visible headers.
  // Reason: the attribute menu should show the same fields users currently see in the grid, including dynamic dataset columns.
  private updateApplicationAttributes(
    visibleColumns?: Array<Pick<FilterBuilderParam, 'id' | 'name'>>
  ): void {
    const fallbackColumns: Array<Pick<FilterBuilderParam, 'id' | 'name'>> = [
      { id: 'APPLICATION_NUMBER', name: 'App #' },
      { id: 'ApplicationSubmittedOn', name: 'App Date' },
      { id: 'WORKFLOW_NAME', name: 'App Type' },
      { id: 'IssuedOn', name: 'Issue Date' },
      { id: 'PARCEL_ADDRESS', name: 'Site Address' },
      { id: 'SUBMITTER', name: 'Applicant' },
      { id: 'PARCEL_OWNER', name: 'Owner' },
      { id: 'APPLICATION_STATUS', name: 'Status' }
    ];
    const sourceColumns =
      visibleColumns && visibleColumns.length > 0
        ? visibleColumns
        : fallbackColumns;
    const configMap = this.getAttributeConfigMap();

    this.applicationAttributes = sourceColumns.sort((a, b) => a.name.localeCompare(b.name)).map(column => ({
      id: column.id,
      name: column.name,
      types: configMap[column.id]?.types || ['contains', 'is'],
      inputType: configMap[column.id]?.inputType,
      options: configMap[column.id]?.options
    }));
  }

  onFilterChanged(newFilters: ItemSearchOptionField[]) {
    this.workflowApplicationsJanusSvc.setData({
      ...this.workflowApplicationsJanusSvc.getData(),
      attributeFilterInputs: newFilters,
      reRunJanusApplicationDataTable: true,
      goToFirstPage:true
    });
    this.storefiltersInLocalStorage();
  }


  // Code added by Sanchit Start 02/25/2026

  myWorkOptions: myWorkOptions[] = [
    {
      name: 'Applications Needing My Attention',
      templateId: 'applicationsNeedingMyAttention',
      displayOrder: 4,
      value: 'needsAttention',
      searchText: null,
      filterText: 'true'
    },
    {
      name: 'Applications Pending Payment',
      templateId: 'pendingPayment',
      displayOrder: 5,
      value: 'PendingPayments',
      searchText: '0',
      strOperator: '>'
    },
    {
      name: 'My Workflow Applications',
      templateId: 'myWorkflowApplications',
      displayOrder: 1,
      value: 'submitter_Id',
      searchText: this.context.user.id
    },
    {
      name: 'Workflow Applications Shared By Me',
      templateId: 'applicationsSharedByMe',
      displayOrder: 2,
      value: 'sharedByMe',
      searchText: this.context.user.userName
    },
    {
      name: 'Workflow Applications Shared With Me',
      templateId: 'applicationsSharedWithMe',
      displayOrder: 3,
      value: 'sharedWithMe',
      searchText: this.context.user.userName
    }
  ];

  handleSearchChange(value: string): void {
    this.searchQuery = value;

    const originalList = this.workflowApplicationsJanusDetails.newApplications;

    // If the search query is empty, reset the displayed list
    if (!value || value.trim() === '') {
      this.workflowApplicationsJanusDetails.displayNewApplications = [
        ...originalList
      ];
      return;
    }

    const searchText = value.toLowerCase().trim();

    this.workflowApplicationsJanusDetails.displayNewApplications = originalList.filter(
      application => application.name.toLowerCase().includes(searchText)
    );
  }
  storefiltersInLocalStorage = () => {
    const currentData = this.workflowApplicationsJanusSvc.getData();
    localStorage.setItem(
      this.FILTER_CACHE_KEY,
      JSON.stringify({
        filterInputs: this.filterInputs || [],
        attributeFilterInputs: currentData.attributeFilterInputs || []
      })
    );
  };
  onExternalFiltersChanged(updated: ItemSearchOptionField[]) {

    this.filterInputs = updated;
    const existing = this.workflowApplicationsJanusSvc.getData();
    this.workflowApplicationsJanusSvc.setData({
      ...existing,
      filterInputs: updated,
      reRunJanusApplicationDataTable: true,
      goToFirstPage:true
    });
    setTimeout(() => {
      this.storefiltersInLocalStorage();
    }, 500);
  }

  ApplicationStatus = ApplicationStatus;
  requestedStatus: ApplicationStatus;
  bulkActionText: string = '';
  now = Date.now();
  pipe = new DatePipe('en-US');
  bulkActionForm: UntypedFormGroup = new UntypedFormGroup({
    bulkActionReason: new UntypedFormControl('', [Validators.required]),
    bulkActionDateTime: new UntypedFormControl({
      value: this.pipe.transform(this.now, 'MM/dd/yyyy h:mm:ss a'),
      disabled: true
    }),
    bulkActionUser: new UntypedFormControl({
      value: this.context.user ? this.context.user.userName : '',
      disabled: true
    })
  });
  bulkActions: BulkAction[] = [];
  selected: any[] = [];

  get bulkActionReason() {
    return this.bulkActionForm.get('bulkActionReason');
  }
  async onBulkActionClick(action: BulkAction) {
    if (!this.selected?.length) return;

    const rows = this.selected.map(id => ({ id }));

    try {
      await action.action(rows);
    } catch (e) {
      console.error('Bulk action failed', e);
    }
  }

  async bulkCloseApplications(applicationIds: string[]) {
    try {
      this.loading = true;

      const bulkActionRequest = new BulkCloseApplicationRequest({
        applicationIds: applicationIds,
        requestedStatus: this.requestedStatus,
        reason: this.bulkActionReason.value,
        dateTime: new Date(),
        user: this.context.user.id
      });

      // await this.dataService
      //   .bulkCloseApplications(bulkActionRequest)
      //   .toPromise();

      this.dataService.bulkCloseApplications(bulkActionRequest).subscribe({
        next: _res => {
          this.toast.success(
            `Application(s) changed to ${
              ApplicationStatus[this.requestedStatus]
            }.`
          );

          // 🔥 2. Stop processing
          this.bulkActionConfirm.cancelProcessing();

          // 🔥 3. Modal close manually
          this.bulkActionConfirm.closeJanusModal();

          // 🔥 4. Table refresh
          this.workflowApplicationsJanusSvc.setData({
            ...this.workflowApplicationsJanusSvc.getData(),
            reRunJanusApplicationDataTable: true
          });

          // 🔥 5. Reset selection
          this.resetBulkSelection();

          // stay on same page
          const currentPage = this.route.snapshot.queryParamMap.get('p');
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              p: currentPage
            },
            queryParamsHandling: 'replace'
          });
        },
        error: _err => {
          this.toast.error('Bulk action failed.Please try again later');
          this.bulkActionConfirm.cancelProcessing();
        }
      });
    } catch (error) {
      this.toast.error('Bulk action failed.Please try again later');
      this.bulkActionConfirm.cancelProcessing();
    } finally {
      this.loading = false;
    }
  }

  resetBulkSelection() {
    //Clear selected IDs
    this.selected = [];

    //  Reset form state
    this.bulkActionForm.reset({
      bulkActionReason: '',
      bulkActionDateTime: this.pipe.transform(
        Date.now(),
        'MM/dd/yyyy h:mm:ss a'
      ),
      bulkActionUser: this.context.user ? this.context.user.userName : ''
    });

    // Tell Data Table to uncheck all checkboxes
    this.workflowApplicationsJanusSvc.setBulkSelection([]);
  }

  private loadBulkActions() {
    this._securitySvc
      ?.isLoginEntitled('WORKFLOW_APPLICATIONS_BULK')
      ?.subscribe(isEntitled => {
        if (!isEntitled) return;

        this.bulkActions.push({
          name: 'Void',
          id: 'void',
          action: async rows => {
            const applicationIds = rows.map(r => r.id);
            this.selected = applicationIds;

            this.requestedStatus = ApplicationStatus.Voided;
            this.bulkActionText = 'Void';

            this.bulkActionForm.controls['bulkActionDateTime'].setValue(
              this.pipe.transform(Date.now(), 'MM/dd/yyyy h:mm:ss a')
            );

            this.bulkActionReason.reset();

            this.bulkActionConfirm.open();

            // this.bulkActionConfirm.open();

            return false;
          }
        });

        this.bulkActions.push({
          name: 'Expire',
          id: 'expire',
          action: async rows => {
            const applicationIds = rows.map(r => r.id);
            this.selected = applicationIds;

            this.requestedStatus = ApplicationStatus.Expired;

            this.bulkActionText = 'Expire';

            this.bulkActionForm.controls['bulkActionDateTime'].setValue(
              this.pipe.transform(Date.now(), 'MM/dd/yyyy h:mm:ss a')
            );

            this.bulkActionReason.reset();

            this.bulkActionConfirm.open();

            return false;
          }
        });

        this.bulkActions.push({
          name: 'Lock',
          id: 'lock',
          action: async rows => {
            const applicationIds = rows.map(r => r.id);
            this.selected = applicationIds;

            this.requestedStatus = ApplicationStatus.Locked;

            this.bulkActionText = 'Lock';

            this.bulkActionForm.controls['bulkActionDateTime'].setValue(
              this.pipe.transform(Date.now(), 'MM/dd/yyyy h:mm:ss a')
            );

            this.bulkActionReason.reset();

            this.bulkActionConfirm.open();
            return false;
          }
        });

        this.bulkActions.push({
          name: 'Withdraw',
          id: 'withdraw',
          action: async rows => {
            const applicationIds = rows.map(r => r.id);
            this.selected = applicationIds;

            this.requestedStatus = ApplicationStatus.Withdrawn;

            this.bulkActionText = 'Withdraw';

            this.bulkActionForm.controls['bulkActionDateTime'].setValue(
              this.pipe.transform(Date.now(), 'MM/dd/yyyy h:mm:ss a')
            );

            this.bulkActionReason.reset();

            this.bulkActionConfirm.open();
            return false;
          }
        });
      });
  }

  ngOnInit(): void {
    const cache = localStorage.getItem(this.FILTER_CACHE_KEY);
    if (cache) {
      try {
        const parsed = JSON.parse(cache);
        this.filterInputs = Array.isArray(parsed.filterInputs)
          ? parsed.filterInputs
          : [];
        const attributeFilters = Array.isArray(parsed.attributeFilterInputs)
          ? parsed.attributeFilterInputs
          : [];
        this.workflowApplicationsJanusSvc.setData({
          ...this.workflowApplicationsJanusSvc.getData(),

          filterInputs: this.filterInputs || [],
          attributeFilterInputs: attributeFilters || []
        });
      } catch (e) {
        console.log(e);
      }
    }
    this.workflowApplicationsJanusSvc.setData({
  ...this.workflowApplicationsJanusSvc.getData(),
  searchText: [],
  // reRunJanusApplicationDataTable: true
});
    // Added by amisha on 2026-07-09 to refresh department-scoped parent state only when the route department changes.
    // Reason: repeated Janus service emissions were causing unnecessary child-side department lookups.
    this.route.parent.paramMap
      .pipe(
        distinctUntilChanged(
          (previous, current) =>
            previous.get('departmentId') === current.get('departmentId')
        )
      )
      .subscribe(params => {
        const departmentId = params.get('departmentId');
        this.getApplicationsBasedOnDepartment(departmentId);
      });
    this.workflowApplicationsJanusSvc.bulkSelection$.subscribe(selection => {
      this.selected = selection;
    });
    this.loadBulkActions();
    this.workflowApplicationsJanusSvc.data$.subscribe(data => {
      this.filterInputs = data.filterInputs;
      this.updateApplicationAttributes(data.visibleAttributeColumns);
    });
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        let currentRoute = this.route;
        // Traverse to deepest child
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        const applicationId = currentRoute.snapshot.paramMap.get(
          'applicationId'
        );
        if (applicationId) {
          this.workflowApplicationsJanusDetails.activeNewApplicationId = applicationId;
        } else {
          this.workflowApplicationsJanusDetails.activeNewApplicationId = '';
        }
      });

        const currentUrl = this.router.url;

        if (currentUrl.includes('/parcels')) {
          this.showAdvancedFilter = true;
          this.placeholder = 'Search Parcels';
        } else {
          this.showAdvancedFilter = false;
          this.placeholder = 'Search Applications';
        }
  }

  // Start code added by Sanchit on 03/13/2026
  getApplicationsBasedOnDepartment = (deptId: string) => {
    const clientId = this.route.parent?.parent?.snapshot.paramMap.get('clientId');
    this.workflowApplicationsJanusDetails.isNewApplicationFetching = true;
    this.workflowApplicationsJanusDetails.errorNewApplicationFetch = '';

    this.dataService
      .getPublishedWorkflowsForAdminJanusApplications(
        clientId,
        [
          WorkflowType.Permit,
          WorkflowType.Other,
          WorkflowType.Renewable
        ],
        false,
        deptId
      )
      .pipe(
        finalize(() => {
          this.workflowApplicationsJanusDetails.isNewApplicationFetching = false;
          this.updateApplicationAttributes(
            this.workflowApplicationsJanusSvc.getData().visibleAttributeColumns
          );
        })
      )
      .subscribe({
        next: response => {
          if (!response || response.length === 0) {
            this.workflowApplicationsJanusDetails.errorNewApplicationFetch =
              'no applications found';
            return;
          }

          this.workflowApplicationsJanusDetails.newApplications = response.map(
            permit => ({
              name: permit?.version?.name || permit?.name,
              value: permit?.id
            })
          );
          this.workflowApplicationsJanusDetails.filterWorkflows = this.workflowApplicationsJanusDetails.newApplications.map(
            app => ({
              name: app.name,
              // changed value to name of workflow as instructed by shawn sir
              value: app.name
            })
          );

          this.workflowApplicationsJanusDetails.displayNewApplications = this.workflowApplicationsJanusDetails.newApplications;
        },

        error: () => {
          this.workflowApplicationsJanusDetails.errorNewApplicationFetch =
            'error fetching applications';
        }
      });
  };
  // End code added by Sanchit on 03/13/2026

  onSearch(value: ItemSearchOptionField[]) {
    localStorage.setItem('workflow-applications-search', JSON.stringify(value));
    const isParcelTab = this.router.url.includes('/parcels');
    this.workflowApplicationsJanusSvc.setData({
      ...this.workflowApplicationsJanusSvc.getData(),
      searchText: value,
      reRunJanusApplicationDataTable: true,
      goToFirstPage: !isParcelTab
    });
  }
  handleParcelIdChange(value: string) {
    // setTimeout(() => {
      this.parcelId = value;
    // });
    // this.workflowApplicationsJanusSvc.setData({
    //   ...this.workflowApplicationsJanusSvc.getData(),
    //   parcelId: value
    // });
  }
  get isDataRoute(): boolean {
    return this.router.url.includes('/data');
  }
  handlePropertyOwnerChange(value: string) {
    // setTimeout(() => {
      this.propertyOwner = value;
    // });
    // this.workflowApplicationsJanusSvc.setData({
    //   ...this.workflowApplicationsJanusSvc.getData(),
    //   propertyOwner: value
    // });
  }
  handlePropertyAddressChange(value: string) {
    // setTimeout(() => {
      this.propertyAddress = value;
    // });
    // this.workflowApplicationsJanusSvc.setData({
    //   ...this.workflowApplicationsJanusSvc.getData(),
    //   propertyAddress: value
    // });
  }

  onAttribute(value: string) {
  }

  onBulk(value: string) {
  }

  onTabButtonClick = (btnTitle: string, tabName: string) => {
    return {
      text: btnTitle,
      onClick: () => {
        if (tabName === 'map') {
          this.showAdvancedFilter = false;
          this.placeholder = 'Search Applications';
          this.router.navigate(['map'], { relativeTo: this.route });
        } else if (tabName === 'parcels') {
          this.showAdvancedFilter = true;
          this.placeholder = 'Search Parcels';
          this.router.navigate(['parcels'], { relativeTo: this.route });
        } else if (tabName === 'data') {
          this.showAdvancedFilter = false;
          this.placeholder = 'Search Applications';
          this.router.navigate(['data'], { relativeTo: this.route });
        }
      }
    };
  };
  onNewApplicationMenuItemClick = (
    newApplicationName: string,
    applicationId: string
  ) => {
    return {
      text: newApplicationName,
      onClick: () => {
        this.startWorkflow(applicationId);
      }
    };
  };
  // Added by Amisha on 2026-07-29.
  // Reason: block duplicate starts, keep loading active through guarded navigation,
  // and show the common error overlay when either start or application-status validation fails.
  startWorkflow(applicationId: string) {
    if (this.isLoading) {
      return;
    }

    this.startApplicationError = false;
    this.isLoading = true;
    const isTestApplication = false;
    const startingActivityId = '1';

    const workflowId = applicationId;
    this.workflowService
      .startApplication(workflowId, isTestApplication, startingActivityId)
      .subscribe({
        next: (response: any) => {
          // ✅ Safely get real applicationId
          const startedApplicationId =
            response?.applicationId ??
            response?.applicationID ??
            response?.applicationld;

          if (!startedApplicationId) {
            this.isLoading = false;
            this.startApplicationError = true;
            return;
          }

          const activeTab =
            this.route.snapshot.firstChild?.url?.[0]?.path || 'data';

          this.router
            .navigate([activeTab, 'application', startedApplicationId], {
              relativeTo: this.route,
              queryParams: {
                p: 1
              },
              queryParamsHandling: 'replace',
              state: {
                startingActivityId,
                useNewLayout: this.context.useNewLayout
              }
            })
            .then(navigationSucceeded => {
              if (!navigationSucceeded) {
                this.startApplicationError = true;
                return;
              }

              if (
                this.router.url.includes(
                  `/application/${startedApplicationId}`
                )
              ) {
                setTimeout(() => {
                  this.context.applicationViewType$.emit('overlay');
                }, 1000);
              }
            })
            .catch(() => {
              this.startApplicationError = true;
            })
            .finally(() => {
              this.isLoading = false;
            });
        },
        error: _err => {
          this.isLoading = false;
          this.startApplicationError = true;
        }
      });
  }

  // Added by Amisha on 2026-07-29.
  // Reason: allow staff to dismiss the start-application error overlay and return to the table.
  dismissStartApplicationError(): void {
    this.startApplicationError = false;
  }

  styleBulkMenu(): void {
  setTimeout(() => {
    const panels = document.querySelectorAll('.mat-mdc-menu-panel');

    const panel = panels[panels.length - 1] as HTMLElement;

    if (panel) {
      panel.style.minWidth = '200px';
      panel.style.borderRadius = '16px';
      panel.style.marginTop = '5px';
      panel.style.overflow = 'hidden';
    }
  });
}
}
